import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheckCircle2, lucideX } from '@ng-icons/lucide';

import { ZardCardComponent } from '../../shared/components/card';

import { PartnersPageComponent } from './partners-page.component';
import { QuestionnaireCompleteComponent } from './questionnaire-complete.component';

interface Question {
  id: string;
  question: string;
}

const QUESTIONS: Question[] = [
  { id: 'comprehensive_insurance', question: 'Avez-vous une assurance tous risques ?' },
  { id: 'vacant_insurance', question: 'Avez-vous une assurance non occupant ?' },
  { id: 'internet_included', question: 'Internet est inclus ?' },
  { id: 'trusted_artisans', question: 'Avez-vous des artisans de confiance à associer au bien ?' },
  { id: 'rent_guarantee', question: 'Garantie loyers impayés (GLI) souhaitée ?' },
];

@Component({
  selector: 'app-property-questionnaire',
  imports: [
    NgIcon,
    ZardCardComponent,
    PartnersPageComponent,
    QuestionnaireCompleteComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideCheckCircle2, lucideX })],
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
  template: `
    @if (showCompletePage()) {
      <app-questionnaire-complete
        [answers]="answers()"
        (accessDashboard)="completed.emit()"
      />
    } @else if (showPartnersPage()) {
      <app-partners-page
        [questionId]="currentQuestion().id"
        [questionText]="currentQuestion().question"
        (back)="handleBackFromPartners()"
        (continued)="handleContinueFromPartners()"
      />
    } @else {
      <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div class="max-w-md w-full">
          <z-card @fadeInUp class="p-8 shadow-xl border-0">
            <div class="text-center mb-8">
              <div class="inline-flex items-center justify-center w-12 h-12 bg-[#1A2E47] rounded-full mb-4 shadow-md">
                <span class="text-white font-bold">{{ currentQuestionIndex() + 1 }}</span>
              </div>
              <h2 class="text-2xl text-gray-900 mb-2">{{ currentQuestion().question }}</h2>
              <p class="text-sm text-gray-500">Question {{ currentQuestionIndex() + 1 }} sur {{ totalQuestions }}</p>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <button
                type="button"
                (click)="handleAnswer('yes')"
                class="p-6 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-600 rounded-xl transition-all group"
              >
                <div class="flex flex-col items-center gap-3">
                  <div class="w-12 h-12 bg-green-600 group-hover:bg-green-700 rounded-full flex items-center justify-center transition-colors">
                    <ng-icon name="lucideCheckCircle2" class="w-6 h-6 text-white" />
                  </div>
                  <span class="text-lg text-gray-900">Oui</span>
                </div>
              </button>

              <button
                type="button"
                (click)="handleAnswer('no')"
                class="p-6 bg-red-50 hover:bg-red-100 border-2 border-red-200 hover:border-red-600 rounded-xl transition-all group"
              >
                <div class="flex flex-col items-center gap-3">
                  <div class="w-12 h-12 bg-red-600 group-hover:bg-red-700 rounded-full flex items-center justify-center transition-colors">
                    <ng-icon name="lucideX" class="w-6 h-6 text-white" />
                  </div>
                  <span class="text-lg text-gray-900">Non</span>
                </div>
              </button>
            </div>

            <div class="mt-6 text-center">
              <button type="button" (click)="handleSkip()" class="text-sm text-gray-500 hover:text-gray-700 hover:underline">
                Passer cette question
              </button>
            </div>

            <z-card class="p-3 bg-blue-50 border-blue-200 mt-4">
              <p class="text-xs text-blue-700 text-center">
                💡 Si vous répondez "Non", nous vous proposerons nos partenaires de confiance
              </p>
            </z-card>
          </z-card>

          <div class="mt-6">
            <div class="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" [style.width.%]="progress()"></div>
            </div>
            <div class="flex justify-between mt-2 text-xs text-gray-500">
              <span>Début</span>
              <span>Question {{ currentQuestionIndex() + 1 }}/{{ totalQuestions }}</span>
              <span>Fin</span>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class PropertyQuestionnaireComponent {
  readonly propertyData = input<unknown>(null);
  readonly completed = output<void>();

  protected readonly totalQuestions = QUESTIONS.length;
  protected readonly currentQuestionIndex = signal(0);
  protected readonly answers = signal<Record<string, string>>({});
  protected readonly showPartnersPage = signal(false);
  protected readonly showCompletePage = signal(false);

  protected readonly currentQuestion = computed(() => QUESTIONS[this.currentQuestionIndex()]);
  protected readonly isLastQuestion = computed(() => this.currentQuestionIndex() === QUESTIONS.length - 1);
  protected readonly progress = computed(
    () => ((this.currentQuestionIndex() + 1) / QUESTIONS.length) * 100,
  );

  protected handleAnswer(answer: 'yes' | 'no'): void {
    const q = this.currentQuestion();
    this.answers.update(a => ({ ...a, [q.id]: answer }));
    if (answer === 'no') {
      this.showPartnersPage.set(true);
    } else {
      this.moveToNextQuestion();
    }
  }

  protected handleSkip(): void {
    const q = this.currentQuestion();
    this.answers.update(a => ({ ...a, [q.id]: 'skipped' }));
    this.moveToNextQuestion();
  }

  protected handleContinueFromPartners(): void {
    this.showPartnersPage.set(false);
    this.moveToNextQuestion();
  }

  protected handleBackFromPartners(): void {
    const q = this.currentQuestion();
    this.answers.update(a => {
      const next = { ...a };
      delete next[q.id];
      return next;
    });
    this.showPartnersPage.set(false);
  }

  private moveToNextQuestion(): void {
    if (this.isLastQuestion()) {
      this.showCompletePage.set(true);
    } else {
      this.currentQuestionIndex.update(i => i + 1);
    }
  }
}
